
import { defineComponent, computed } from "vue";
import DropdownMenu from "../Components/DropdownMenu.vue";
import PlayerStatus from "./PlayerStatus.vue";
import gameApplication from "../../game/game-app";
import { useStore } from "../../stores";
import { MutationType } from "../../stores/mutation-types";
import { Player } from "../../game/player";
import { Assets } from "@/game/resources";
import { IAssetsConfig } from "@/types/game-config-type";
import { GameConfig } from "@/config/game-config";
import { HitResultEnum } from "@/types/hit-result-enum";

const sounds: IAssetsConfig = GameConfig.sounds;

export default defineComponent({
  name: "GameStatus",

  props: {
    // this is the "game type" and can have the value "PRACTICE"
    gameMode: { type: String, required: false },
  },

  data() {
    return {
      ballIconPath: "/images/ball-icons/",
      player1ElapsedTime: 0,
      player2ElapsedTime: 0,
      player1Timer: undefined,
      player2Timer: undefined,
      inactiveOpponentTimer: undefined,
      timerList: [],
      gameIcon: "",
    };
  },

  setup() {
    const store = useStore();
    const player1 = computed(() => store.state.player1);
    const player2 = computed(() => store.state.player2);
    const playTurn = computed(() => store.state.playTurn);

    return {
      player1,
      player2,
      playTurn,
    };
  },

  mounted() {
    gameApplication.onEvent('START_GAME', this.startGame);
    gameApplication.onEvent('OK', this.startTimer);
    gameApplication.onEvent('PLAYER_CHANGE', this.startTimer);
    gameApplication.onEvent('HIT_CUE_BALL', this.stopTimers);
    gameApplication.onEvent('OPPONENT_HIT_CUE_BALL', this.stopTimers);
    gameApplication.onEvent('PASS_BACK', this.stopTimers);
    gameApplication.onEvent('END_GAME', this.endGame);

    // get the game icon path
    this.gameIcon = this.getGameIcon();
  },

  methods: {
    startGame(): void {   
      // The game icon is fetched again as there can be a delay to get the game type. 
      this.gameIcon = this.getGameIcon();  
      
      this.startTimer();     
    },

    startTimer(): void {
      if (gameApplication.getGameKind() !== 'PRACTICE') { // 2-player game
        const players = gameApplication.getBillardGame().getPlayers();
        if (players[0].playerId === gameApplication.getPlayer().playerId) { // is player 1
          if (this.playTurn === gameApplication.getPlayer().playerId) {
            this.startPlayer1Timer();
          } else { 
            this.startPlayer2Timer();      
          }
        } else { // is player 2
          if (this.playTurn === gameApplication.getPlayer().playerId) {
            this.startPlayer2Timer();
          } else {
            this.startPlayer1Timer();
          }  
        }
        
        // allow the turn player to move the cue stick to shoot
        gameApplication.getBillardGame().stick.setMoveable(gameApplication.getBillardGame().isOpponentTurn());
      } 
      else { // practice game
        this.playTurn = gameApplication.getPlayer().playerId;
      }
    },

    startPlayer1Timer(): void {
      this.player2ElapsedTime = 0;
      const timeLimit = gameApplication.getGameItem().timeLimit;
      let startTime = Date.now();
      let elapsedTime = startTime;
      this.player1Timer = setInterval(() => this.player1HitTimer(elapsedTime, startTime, timeLimit), 1000); // this should be a 1 second interval 
      this.timerList.push(this.player1Timer);
    },

    startPlayer2Timer(): void {
      this.player1ElapsedTime = 0;
      const timeLimit = gameApplication.getGameItem().timeLimit;
      let startTime = Date.now();
      let elapsedTime = startTime;
      this.player2Timer = setInterval(() => this.player2HitTimer(elapsedTime, startTime, timeLimit), 1000); // this should be a 1 second interval
      this.timerList.push(this.player2Timer);
    },

    player1HitTimer(elapsedTime: number, startTime: number, timeLimit: number): void {
      elapsedTime = Date.now() - startTime;
      let seconds = Math.round((elapsedTime / 1000));
      this.player1ElapsedTime = timeLimit - seconds;

      if (this.player1ElapsedTime >= 1 && this.player1ElapsedTime <= 5) {
        Assets.playSound(sounds.paths.TIMER_BEEP, 0.5);
      }
      if (this.player1ElapsedTime == 0) { // miss turn
        this.stopTimers();
        this.handleTimeout(this.player1);
      }
    },

    player2HitTimer(elapsedTime: number, startTime: number, timeLimit: number): void {  
      elapsedTime = Date.now() - startTime;
      let seconds = Math.round((elapsedTime / 1000));
      this.player2ElapsedTime = timeLimit - seconds;

      if (this.player2ElapsedTime >= 1 && this.player2ElapsedTime <= 5) {
        Assets.playSound(sounds.paths.TIMER_BEEP, 0.5);
      }
      if (this.player2ElapsedTime == 0) { // miss turn
        this.stopTimers();
        this.handleTimeout(this.player2);
      }
    },

    inactiveOpponentEndGameTimer(): void {
      this.stopTimers();
    },

    stopTimers(): void {
      clearInterval(this.player1Timer);
      clearInterval(this.player2Timer);
      clearInterval(this.inactiveOpponentTimer);
    
     /*
      * Clear any previous timers that were not cleared.
      * This is needed as sometimes the previous timers are not cleared.
      */ 
      for (let i = 0; i < this.timerList.length; i++) {
        clearInterval(this.timerList[i]);
      }
    },

    handleTimeout(player: Player): void {
      const store = useStore();
      store.commit(MutationType.GAME_MESSAGE, `Player ${ player.playerId } hit time limit exceeded (foul)`);
      gameApplication.setMissedTurnFoul(true);
      
      if (!gameApplication.missedTurnLimit()) {
        gameApplication.getBillardGame().lastHitResult = HitResultEnum.PLAYER_CHANGE;
        gameApplication.clearSynchro();
        gameApplication.getBillardGame().continueGame();
      }
    },
    
    /**
     * Clear the values at the end of a game.
     */
    endGame(): void {
      // stop the timers
      this.stopTimers();

      const store = useStore();

      // clear the player turn
      store.commit(MutationType.PLAY_TURN, null);

      // clear player 1
      let emptyPlayer1: Player = gameApplication.getEmptyPlayer();
      try {
        emptyPlayer1.player = this.player1;
        emptyPlayer1.player.ball = 'default.svg';
        emptyPlayer1.score = 0;
        emptyPlayer1.enablePassback = false;
      } catch (err) {
        console.log("Cannot clear the player", err);
      }
      store.commit(MutationType.PLAYER1, emptyPlayer1);

      if (gameApplication.getGameKind() !== 'PRACTICE') {
        // clear player 2
        let emptyPlayer2: Player = gameApplication.getEmptyPlayer();
        try {   
          emptyPlayer2.player = this.player2;
          emptyPlayer2.player.ball = 'default.svg';
          emptyPlayer2.score = 0;
          emptyPlayer2.enablePassback = false;
        } catch (err) {
          console.log("Cannot clear the player", err);
        }
        store.commit(MutationType.PLAYER2, emptyPlayer2);
      }

      // reset the elapsed time to zero 
      this.player1ElapsedTime = 0;
      this.player2ElapsedTime = 0;

      gameApplication.getBillardGame().clearGameValues();

      // stop the game animation
      if (gameApplication.requestId !== undefined) {
        gameApplication.stopAnimate();
      }
    },

    getTimerDisplayFormat(timeInSeconds: number): string {  
      if (timeInSeconds === 0) {
        return '0:00';
      } 
      else if (timeInSeconds < 10) {
        return '0:0' + timeInSeconds;
      } 
      else if (timeInSeconds < 60) {
        return '0:' + timeInSeconds;
      } 
      else { // the time is greater than one minute
        let seconds = timeInSeconds - 60;
        let secs = seconds < 10 ? '0' + seconds : seconds; 
        return '1:' + secs;
      }
    },

    getBall(p: Player): string {
      if (p) {
        if (!p.player) {
          return 'default.svg'; 
        } else if (!p.player.ball) {
          return 'default.svg';
        } else {
          return p.player.ball;
        }
      } else {
        return 'default.svg'; 
      }
    },

    getGameIcon(): string {
      const gameKind = gameApplication.getGameKind();
      const gameIconPath = '/images/game-logos/';

      if (gameKind === 'PRACTICE') {
        return gameIconPath + 'practice.svg';
      } else if (gameKind === 'POOL8') {
        return gameIconPath + '8pool.svg';
      } else if (gameKind === 'POOL9') {
        return gameIconPath + '9pool.svg';
      } else if (gameKind === 'POOL8UK') {
        return gameIconPath + 'uk8pool.svg';
      } else if (gameKind === 'SNOOKER') {
        return gameIconPath + 'snooker.svg';
      } else if (gameKind === 'SNOOKER6') {
        return gameIconPath + '6snooker.svg';
      } else { // this shouldn't happen
        return gameIconPath + 'practice.svg';
      }
    },

    getGameKind(): string {
      return gameApplication.getGameKind();
    },

    getDisplayScore(): boolean {
      const gameKind = gameApplication.getGameKind();
      
      if (gameKind === 'PRACTICE') {
        return false;
      } else if (gameKind === 'POOL8') {
        return false;
      } else if (gameKind === 'POOL9') {
        return false;
      } else if (gameKind === 'POOL8UK') {
        return false;
      } else if (gameKind === 'SNOOKER') {
        return true;
      } else if (gameKind === 'SNOOKER6') {
        return true;
      } else { // this shouldn't happen
        return false;
      }
    }
  },

  components: {
    DropdownMenu,
    PlayerStatus,
  },
});
